/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Location, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  effect,
  HostBinding,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { map } from 'rxjs';

import { UButtonComponent } from '@usheru-hff/core/button/button.component';
import { GsapAnimationService, GsapPointerRevealVariables } from '@usheru-hff/core/gsap';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { SectionLinkService } from '@usheru-hff/sdk/movie-site';
import { TRACKING_SERVICE } from '@usheru-hff/sdk/tracking';
import { getChannelMovieProp, Movie } from '@usheru-hff/shared/domain';
import { LoadingIndicatorComponent } from '@usheru-hff/shared/ui-loading-indicator';

import { NavAction, NavCommand, NavComponent, NavLink } from './movie-site-nav-action.model';
import { MovieSiteNavActionService } from './movie-site-nav-action.service';

const NAV_ACTION_HOVER_REVEAL_VARIABLES: GsapPointerRevealVariables = {
  x: '--movie-site-nav-action-hover-reveal-x',
  y: '--movie-site-nav-action-hover-reveal-y',
  size: '--movie-site-nav-action-hover-reveal-size'
};

@Component({
  standalone: true,
  imports: [NgClass, FontAwesomeModule, UButtonComponent, LoadingIndicatorComponent, NgTemplateOutlet],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'movie-site-nav-action',
  templateUrl: './movie-site-nav-action.component.html',
  styleUrls: ['./movie-site-nav-action.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [SectionLinkService]
})
export class MovieSiteNavActionComponent implements OnInit {
  readonly viewContainerRef = inject(ViewContainerRef);
  readonly channelService = inject(ChannelService);
  readonly sectionService = inject(SectionLinkService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly location = inject(Location);
  readonly navActionService = inject(MovieSiteNavActionService);
  private readonly trackingService = inject(TRACKING_SERVICE);
  private readonly gsapAnimationService = inject(GsapAnimationService);

  @HostBinding('class') classes = '';

  private readonly hoverRevealState = new WeakMap<HTMLElement, 'entered' | 'left'>();

  readonly componentPlaceRef = viewChild('componentPlace', { read: ViewContainerRef });
  readonly movie = toSignal(
    this.route.data.pipe(
      takeUntilDestroyed(),
      map(data => data['movie'] as Movie | null | undefined)
    ),
    {
      initialValue: this.route.snapshot.data['movie'] as Movie | null | undefined
    }
  );

  navAction = input.required<NavAction>();
  navLink = computed(() => this.navAction() as NavLink);
  navCommand = computed(() => this.navAction() as NavCommand);

  /**
   * When navAction is type 'link'
   */
  isRouteActive = signal<boolean>(false);

  private thisRoute = this.router.createUrlTree([], { relativeTo: this.route }).toString();

  href = computed(() => {
    const thisRoute = this.thisRoute;
    const ogNavLink = this.navAction() as NavLink;
    let navLink: string[] = [...thisRoute.split('/')];
    if (typeof ogNavLink.url === 'string') {
      navLink = [...navLink, ...ogNavLink.url.split('/')];
    } else {
      navLink = [...navLink, ...ogNavLink.url];
    }
    // reduce to remove the '..' and the item before it
    if (navLink.includes('..')) {
      navLink = navLink.reduce((acc, item) => {
        if (item === '..') {
          acc.pop();
        } else if (item !== '.') {
          acc.push(item);
        }
        return acc;
      }, [] as string[]);
    }

    if (ogNavLink.fragment) {
      return `${navLink.join('/')}#${ogNavLink.fragment}`;
    } else {
      return navLink.join('/');
    }
  });

  styleClasses = computed(
    () =>
      ({
        'movie-site_nav-action_menu-action': true,
        'hover-reveal-enabled': this.navActionHoverRevealEnabled(),
        fill: this.navAction().style === 'button-fill',
        'not-fill': this.navAction().style === 'button-not-fill',
        ...(this.navAction().extraClasses || {})
      }) as {
        [klass: string]: boolean;
      }
  );
  iconProp = computed(() => this.navAction().icon as IconProp);

  navActionHoverRevealEnabled = computed(() => {
    const style = this.navAction().style;
    if (style !== 'button-fill' && style !== 'button-not-fill') {
      return false;
    }

    const movie = this.movie();
    if (!movie) {
      return false;
    }

    return getChannelMovieProp('movieSite.gsap.navActionHoverReveal', this.channelService.channel, movie) ?? false;
  });

  isExternalLink = computed(() => {
    const navLink = this.navAction() as NavLink;
    if (typeof navLink.url === 'string' && (navLink.url as string).startsWith('http')) {
      return true;
    }
    return false;
  });

  private componentRef = signal<ComponentRef<any> | null>(null);
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const vcr = this.componentPlaceRef();
      const nav = this.navAction() as NavComponent | null;

      // Bind extra classes to host element
      this.classes = Object.keys(this.navAction().extraClasses || [])
        .filter(key => this.navAction().extraClasses![key])
        .join(' ');

      if (!vcr || !nav || nav.type !== 'component' || !nav.component) return;

      const cmp = nav.component;
      const inputs = nav.binding ?? {};

      // (1) If component type changed, recreate (clear old one)
      const existing = this.componentRef();
      if (!existing || existing.componentType !== cmp) {
        vcr.clear(); // 🔑 prevents multiple instances
        const ref = vcr.createComponent(cmp);
        this.componentRef.set(ref);

        // Ensure destroy when parent is destroyed
        this.destroyRef.onDestroy(() => ref.destroy());
      }

      // (2) Always update inputs (no need to recreate)
      const ref = this.componentRef()!;
      for (const [key, value] of Object.entries(inputs)) {
        ref.setInput(key, value);
      }
    });
  }
  ngOnInit(): void {
    if (this.navAction().type === 'link') {
      this.isRouteActive.set(this.navActionService.checkNavActionIsActive(this.navLink()));
      this.navActionService.routeChangeEmmitter.subscribe(() => {
        this.isRouteActive.set(this.navActionService.checkNavActionIsActive(this.navLink()));
      });
    }
  }

  handleRedirection = (e: PointerEvent) => {
    e.preventDefault();
    const navLink = this.navAction() as NavLink;
    const fragment = navLink.fragment;
    // If the link is an empty array and has a fragment, scroll to that section
    if (Array.isArray(navLink.url) && navLink.url.length === 0 && fragment) {
      this.sectionService.goToSection(fragment);
    } else {
      let navigateUrl = navLink.url;
      if (typeof navigateUrl === 'string') {
        navigateUrl = [navigateUrl];
      }
      this.router
        .navigate(navigateUrl, {
          relativeTo: this.route,
          fragment: fragment
        })
        .then(() => {
          if (navLink.fragment === 'landing') {
            this.location.replaceState(this.router.url.split('#')[0]);
          }
        });
    }
    // if (navLink.tracking) {
    //   this.trackingService.trackEvent(navLink.tracking);
    // }
  };

  handleExternalRedirection = () => {
    // const navLink = this.navAction() as NavLink;
    // if (navLink.tracking) {
    //   this.trackingService.trackEvent(navLink.tracking);
    // }
  };

  animateHoverReveal = async (event: PointerEvent | FocusEvent) => {
    if (!this.gsapAnimationService.canAnimate() || !this.navActionHoverRevealEnabled()) return;

    const elements = this.getHoverRevealElements(event);
    if (!elements) return;

    const { button, reveal } = elements;
    await this.gsapAnimationService.setPointerRevealGeometry(button, reveal, event, {
      fillContainer: true,
      variables: NAV_ACTION_HOVER_REVEAL_VARIABLES
    });
    this.hoverRevealState.set(reveal, 'entered');

    await this.gsapAnimationService.loadGsap();
    if (this.hoverRevealState.get(reveal) !== 'entered') return;

    await this.gsapAnimationService.setPointerRevealVisible(reveal, true);
  };

  moveHoverReveal = async (event: PointerEvent) => {
    if (!this.gsapAnimationService.canAnimate() || !this.navActionHoverRevealEnabled()) return;

    const elements = this.getHoverRevealElements(event);
    if (!elements || this.hoverRevealState.get(elements.reveal) !== 'entered') return;

    await this.gsapAnimationService.setPointerRevealGeometry(elements.button, elements.reveal, event, {
      animate: true,
      fillContainer: true,
      variables: NAV_ACTION_HOVER_REVEAL_VARIABLES
    });
  };

  resetHoverReveal = async (event: PointerEvent | FocusEvent) => {
    if (!this.gsapAnimationService.canAnimate() || !this.navActionHoverRevealEnabled()) return;

    const elements = this.getHoverRevealElements(event);
    if (!elements) return;

    const { button, reveal } = elements;
    this.hoverRevealState.set(reveal, 'left');
    await this.gsapAnimationService.setPointerRevealGeometry(button, reveal, event, {
      animate: true,
      fillContainer: true,
      variables: NAV_ACTION_HOVER_REVEAL_VARIABLES
    });

    await this.gsapAnimationService.loadGsap();
    if (this.hoverRevealState.get(reveal) !== 'left') return;

    await this.gsapAnimationService.setPointerRevealVisible(reveal, false);
  };

  private getHoverRevealElements(event: Event): { button: HTMLElement; reveal: HTMLElement } | null {
    const host = event.currentTarget as HTMLElement | null;
    const button = host?.querySelector<HTMLElement>('.u-button');
    const reveal = host?.querySelector<HTMLElement>('[data-movie-site-nav-action-hover-reveal]');

    if (!button || !reveal) return null;

    return { button, reveal };
  }

  handleCommand = (e: PointerEvent) => {
    e.preventDefault();
    const navCommand = this.navAction() as NavCommand;
    navCommand.command();
    // if (navCommand.tracking) {
    //   this.trackingService.trackEvent(navCommand.tracking);
    // }
  };

  getExternalHref() {
    const navLink = this.navAction() as NavLink;
    return typeof navLink.url === 'string' ? navLink.url : navLink.url.join('/');
  }
}
